/**
 * The pick cards the intro's rules beat teaches with (specs/champions-league, Story 9).
 *
 * The World Cup intro shows the card the player is about to use instead of describing it, and
 * that is the part of it worth having. These are the club-football equivalents: real
 * `ApiMatch` shapes fed to the same `SeasonMatchPickCard` a tournament actually renders, so the
 * tutorial cannot show an affordance the product does not have.
 *
 * Anchored to **now** rather than to fixed dates: a hardcoded 2026 kickoff turns every "upcoming"
 * preview into a past match the moment the season moves on, and the card would then teach the
 * wrong state (specs/competitions-hub-client — the same trap the hub story fixtures hit).
 */

import type { ApiMatch } from '../../../src/worldcup/helpers/worldcupPicksData.helper';

const HOUR_MS = 60 * 60 * 1000;

/** Every field the pick card reads; the preview differs only in the few that matter per rule. */
function previewMatch(overrides: Partial<ApiMatch> & Pick<ApiMatch, 'matchId'>): ApiMatch {
  return {
    competitionMatchId: `${overrides.matchId}-cm`,
    homeTeamId: `${overrides.matchId}-home`,
    awayTeamId: `${overrides.matchId}-away`,
    homeTeam: 'Real Madrid',
    awayTeam: 'Bayern München',
    homeCountryCode: '',
    awayCountryCode: '',
    kickoffTime: new Date(Date.now() + 3 * HOUR_MS).toISOString(),
    status: 'upcoming',
    stage: 'REGULAR_SEASON',
    stageLabel: 'Matchday 1',
    round: '1',
    displayOrder: 1,
    homeScore: null,
    awayScore: null,
    liveMinute: null,
    isPickEditable: true,
    prediction: null,
    hasGoldenBall: false,
    isGoldenBallLocked: false,
    ...overrides,
  };
}

/**
 * Built fresh per call so the kickoff stays three hours out however long the app has been open.
 */
export function getCompetitionIntroPreviewMatches(): {
  countdown: ApiMatch;
  boost: ApiMatch;
  live: ApiMatch;
} {
  return {
    countdown: previewMatch({
      matchId: 'intro-countdown',
      homeTeam: 'Real Madrid',
      awayTeam: 'Bayern München',
      prediction: { home: 2, away: 1, pointsEarned: null },
    }),
    boost: previewMatch({
      matchId: 'intro-boost',
      homeTeam: 'Arsenal',
      awayTeam: 'Barcelona',
      stageLabel: 'Matchday 2',
      round: '2',
      displayOrder: 2,
      prediction: { home: 1, away: 1, pointsEarned: null },
      hasGoldenBall: true,
    }),
    live: previewMatch({
      matchId: 'intro-live',
      homeTeam: 'Liverpool',
      awayTeam: 'Inter',
      stageLabel: 'Matchday 3',
      round: '3',
      displayOrder: 3,
      kickoffTime: new Date(Date.now() - 2 * HOUR_MS).toISOString(),
      status: 'live',
      homeScore: 3,
      awayScore: 1,
      liveMinute: 90,
      isPickEditable: false,
      prediction: { home: 3, away: 1, pointsEarned: null },
    }),
  };
}

/** Numbered step badge + preview card + handwritten note; the plain icon cards keep the rest. */
export const COMPETITION_WELCOME_PREVIEW_FRAME_RADIUS = 24;
